/**
 * §12 «Корзина и checkout» (design-guest v7, route /menu/order, guest).
 *
 * H1 «Ваш заказ» + row-cards позиций (название × qty, цена × qty, итог,
 * «убрать»), «Куда подать?» (В номер / Стол / Ресторан), «Пожелания»,
 * summary (Позиции / Доставка бесплатно / Итого к оплате при выезде) +
 * дисклеймер, «Подтвердить заказ» (disabled если пусто) → создаёт заявку →
 * toast → к статусу. Empty: «В Вашем заказе пока ничего нет…» + переход в /menu.
 * Назад ‹ → меню.
 *
 * Корзина = ОДНА заявка (#74): один сабмит → РОВНО одна заявка
 * POST /v1/requests со ВСЕМИ позициями (items[]). Раньше каждая строка
 * уходила отдельной заявкой — теперь один заказ = одна карточка на канбане
 * со списком позиций. primary-категория заявки = F&B/кухня (для роутинга
 * по отделу и видимости в очереди «Заказы по ресторану»). Место подачи и
 * пожелания — в description.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@hsc/ui';
import {
  formatPriceMinor,
  ServiceCategoryId,
  ServiceItemId,
  type ServiceRequestCreateInput,
  type ServiceRequestItemInput,
} from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useCart, type CartLine } from './useCart.js';
import { useCreateOrder } from './useMenu.js';

type DeliveryChoice = 'table' | 'room';

interface DeliveryInfo {
  choice: DeliveryChoice;
  notes?: string;
}

// «За стол» — по умолчанию (первый). Без номера стола, без «Ресторана».
const DELIVERY_OPTIONS: ReadonlyArray<{ value: DeliveryChoice; label: string }> = [
  { value: 'table', label: 'За стол' },
  { value: 'room', label: 'В номер' },
];

function deliveryLabel(d: DeliveryInfo): string {
  return d.choice === 'table' ? 'за стол' : 'в номер';
}

/** Название позиции для строки заказа (с вариантом, если есть). */
function lineTitle(line: CartLine): string {
  return line.variantName !== undefined ? `${line.itemName} — ${line.variantName}` : line.itemName;
}

/** Одна строка корзины → структурная позиция заявки (#74). */
function lineToItem(line: CartLine): ServiceRequestItemInput {
  return {
    item_id: ServiceItemId(line.itemId),
    category_id: ServiceCategoryId(line.categoryId),
    name: line.itemName,
    ...(line.variantName !== undefined && { variant: line.variantName }),
    qty: line.qty,
    ...(line.unitPriceMinor !== undefined && { unit_price_minor: line.unitPriceMinor }),
    ...(line.currency !== undefined && { currency: line.currency }),
  };
}

/**
 * Корзина → ОДНА заявка (#74). primary-категория = F&B/кухня для роутинга:
 * берём категорию первой кухонной позиции (по коду), иначе — категорию
 * первой строки. Все позиции уходят в items[]; место подачи и пожелания —
 * в description; в title — сводка «Заказ из меню (N позиций)».
 */
function cartToRequest(lines: readonly CartLine[], delivery: DeliveryInfo): ServiceRequestCreateInput {
  const where = deliveryLabel(delivery);
  const kitchenLine = lines.find((l) => /(kitchen|кухн|breakfast|завтрак|обед|ужин|горяч)/i.test(`${l.categoryCode} ${l.categoryName}`));
  const primary = kitchenLine ?? lines[0];
  const totalQty = lines.reduce((s, l) => s + l.qty, 0);
  const lineSummary = lines
    .map((l) => `${lineTitle(l)} ×${l.qty}`)
    .join('; ');
  const wishes =
    delivery.notes !== undefined && delivery.notes.trim() !== ''
      ? ` Пожелания: ${delivery.notes.trim()}.`
      : '';
  return {
    category_id: ServiceCategoryId(primary?.categoryId ?? ''),
    title: `Заказ из меню (${lines.length} ${pluralPositions(lines.length)}, ${totalQty} шт.)`,
    description: `Заказ (подать ${where}): ${lineSummary}.${wishes}`,
    items: lines.map(lineToItem),
  };
}

/** «позиция/позиции/позиций» по числу (для сводного title). */
function pluralPositions(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'позиция';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'позиции';
  return 'позиций';
}

export function GuestMenuOrder(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const cart = useCart();
  const order = useCreateOrder();

  // «За стол» выбрано по умолчанию.
  const [delivery, setDelivery] = useState<DeliveryChoice>('table');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [placed, setPlaced] = useState(false);

  const total =
    cart.currency !== undefined ? formatPriceMinor(cart.totalMinor, cart.currency, locale) : null;

  // Empty cart → редирект в меню (design §12 «Empty cart»). Не редиректим
  // после успешного оформления (там свой экран-подтверждение).
  useEffect(() => {
    if (cart.count === 0 && !placed) {
      const t = setTimeout(() => navigate('/menu'), 2500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [cart.count, placed, navigate]);

  async function confirm(): Promise<void> {
    setErrors([]);
    if (cart.lines.length === 0) return;
    const info: DeliveryInfo = {
      choice: delivery,
      ...(notes.trim() !== '' && { notes: notes.trim() }),
    };
    // ОДИН сабмит = ОДНА заявка со всеми позициями (#74).
    try {
      await order.mutateAsync(cartToRequest(cart.lines, info));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'неизвестная ошибка';
      setErrors([`Не удалось оформить заказ: ${msg}`]);
      return;
    }
    cart.clear();
    setPlaced(true);
  }

  if (placed) {
    return (
      <section data-testid="menu-order-success">
        <Toast message="Заказ принят, готовится ~25 мин" />
        <Card>
          <h2 className="hsc-card__title">Спасибо за заказ!</h2>
          <p>Мы передали заказ на кухню. Статус Вы найдёте в разделе «Мой счёт» и «Мои заявки».</p>
          <div className="rq-actions">
            <Button onClick={() => navigate('/menu')}>Заказать ещё</Button>
            <Button variant="secondary" onClick={() => navigate('/folio')}>
              К моему счёту
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  if (cart.count === 0) {
    return (
      <section data-testid="menu-order-empty">
        <button type="button" className="fnb-back" onClick={() => navigate('/menu')} data-testid="menu-back">
          ‹ Меню
        </button>
        <Card>
          <p>В Вашем заказе пока ничего нет. Загляните в меню →</p>
          <div className="rq-actions">
            <Button onClick={() => navigate('/menu')}>В меню</Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="menu-order-page">
      <div className="fnb-cat-head">
        <button type="button" className="fnb-back" onClick={() => navigate('/menu')} data-testid="menu-back">
          ‹ Меню
        </button>
        <h1 className="hsc-page__title">Ваш заказ</h1>
      </div>

      {errors.length > 0 && (
        <div className="ta-error-summary" role="alert" data-testid="order-errors">
          <strong>Не удалось оформить заказ:</strong>
          <ul>
            {errors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      <ul className="fnb-order-lines" data-testid="order-lines">
        {cart.lines.map((line) => (
          <li className="fnb-order-line" key={line.key} data-testid={`order-line-${line.key}`}>
            <div className="fnb-order-line__text">
              <span className="fnb-order-line__name">
                {line.itemName}
                {line.variantName !== undefined && ` · ${line.variantName}`} × {line.qty}
              </span>
              {line.unitPriceMinor !== undefined && line.currency !== undefined && (
                <span className="fnb-order-line__unit">
                  {formatPriceMinor(line.unitPriceMinor, line.currency, locale)} × {line.qty}
                </span>
              )}
            </div>
            {line.unitPriceMinor !== undefined && line.currency !== undefined && (
              <span className="fnb-order-line__sum">
                {formatPriceMinor(line.unitPriceMinor * line.qty, line.currency, locale)}
              </span>
            )}
            <button
              type="button"
              className="fnb-order-line__remove"
              data-testid={`order-line-${line.key}-remove`}
              onClick={() => cart.remove(line.key)}
            >
              убрать
            </button>
          </li>
        ))}
      </ul>

      <fieldset className="fnb-deliver" data-testid="order-delivery">
        <legend className="fnb-deliver__legend">Куда подать?</legend>
        <div className="fnb-deliver__opts" role="radiogroup" aria-label="Куда подать">
          {DELIVERY_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={delivery === opt.value ? 'fnb-deliver__opt is-active' : 'fnb-deliver__opt'}
              data-testid={`order-delivery-${opt.value}`}
            >
              <input
                type="radio"
                name="fnb-delivery"
                value={opt.value}
                checked={delivery === opt.value}
                onChange={() => setDelivery(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <label className="fnb-wishes">
        <span className="fnb-wishes__label">Пожелания (необязательно)</span>
        <textarea
          className="fnb-wishes__input"
          rows={2}
          placeholder="Например: без лука, принести позже"
          data-testid="order-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      <div className="fnb-summary" data-testid="order-summary">
        <div className="fnb-summary__row">
          <span>Позиции ({cart.count})</span>
          <span>{total ?? '—'}</span>
        </div>
        <div className="fnb-summary__row">
          <span>Доставка</span>
          <span>бесплатно</span>
        </div>
        <div className="fnb-summary__row fnb-summary__row--total">
          <strong>Итого к оплате при выезде</strong>
          <strong data-testid="order-total">{total ?? '—'}</strong>
        </div>
        <p className="fnb-summary__note">Оплата при выезде — заказ добавится к Вашему счёту.</p>
      </div>

      <div className="rq-actions">
        <Button
          loading={order.isPending}
          data-testid="order-submit"
          disabled={cart.count === 0 ? true : undefined}
          onClick={() => void confirm()}
        >
          Подтвердить заказ
        </Button>
      </div>
    </section>
  );
}
